import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

export function AdjustDialog({ product, wh, onClose, onDone }) {
  const [mode, setMode] = useState("add");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!qty || Number(qty) < 0) return toast.error("Enter a valid quantity");
    if (!reason.trim()) return toast.error("A reason is required");
    setSaving(true);
    try { await api.post("/inventory/adjust", { product_id: product.id, warehouse_id: wh, mode, quantity: Number(qty), reason }); toast.success("Stock adjusted"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="sm:max-w-sm" data-testid="inv-adjust-dialog">
      <DialogHeader><DialogTitle>Adjust stock · {product.code}</DialogTitle><DialogDescription>Current available: {product.available} {product.unit}. Every change is logged.</DialogDescription></DialogHeader>
      <div className="space-y-3 py-1">
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Action</Label><Select value={mode} onValueChange={setMode}><SelectTrigger className="mt-1" data-testid="inv-adjust-mode"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="add">Add stock</SelectItem><SelectItem value="reduce">Reduce stock</SelectItem><SelectItem value="set">Set exact</SelectItem></SelectContent></Select></div>
          <div><Label>Quantity</Label><Input type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1" data-testid="inv-adjust-qty" /></div>
        </div>
        <div><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Opening stock, stock count, damage" className="mt-1" data-testid="inv-adjust-reason" /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="inv-adjust-save">{saving ? "Saving…" : "Save"}</Button></DialogFooter>
    </DialogContent></Dialog>);
}

export function TransferDialog({ product, warehouses, fromWh, onClose, onDone }) {
  const [from, setFrom] = useState(fromWh);
  const [to, setTo] = useState(warehouses.find((w) => w.id !== fromWh)?.id || "");
  const [qty, setQty] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (from === to) return toast.error("Choose two different warehouses");
    if (!qty || Number(qty) <= 0) return toast.error("Enter a valid quantity");
    setSaving(true);
    try { await api.post("/inventory/transfer", { product_id: product.id, from_warehouse_id: from, to_warehouse_id: to, quantity: Number(qty) }); toast.success("Stock transferred"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="sm:max-w-sm" data-testid="inv-transfer-dialog">
      <DialogHeader><DialogTitle>Transfer · {product.code}</DialogTitle><DialogDescription>Move stock between warehouses.</DialogDescription></DialogHeader>
      <div className="space-y-3 py-1">
        <div><Label>From</Label><Select value={from} onValueChange={setFrom}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>To</Label><Select value={to} onValueChange={setTo}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Quantity</Label><Input type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1" data-testid="inv-transfer-qty" /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="inv-transfer-save">{saving ? "Saving…" : "Transfer"}</Button></DialogFooter>
    </DialogContent></Dialog>);
}

export function HistoryDialog({ product, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/inventory/product/${product.id}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, [product.id]);
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="sm:max-w-md" data-testid="inv-history-dialog">
      <DialogHeader><DialogTitle>{product.name}</DialogTitle><DialogDescription>Stock by warehouse and movement history.</DialogDescription></DialogHeader>
      {!d ? <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#6B7189]" /></div> : (
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          <div className="space-y-1">{d.stock.map((s) => (
            <div key={s.id} className="flex justify-between text-sm bg-[#F8F9FC] rounded-lg px-3 py-2">
              <span className="text-[#101D51]">{s.warehouse?.name || "Warehouse"}</span>
              <span className="font-semibold">{s.available_quantity} avail · {s.reserved_quantity} reserved</span>
            </div>))}</div>
          <div className="divide-y divide-[#F0F1F5]">{d.movements.length === 0 ? <div className="text-sm text-[#6B7189] py-2">No movements.</div> : d.movements.map((m) => (
            <div key={m.id} className="flex justify-between py-2 text-sm">
              <div><div className="text-[#101D51]">{m.movement_type}</div><div className="text-[11px] text-[#6B7189]">{m.warehouse?.name} · {new Date(m.created_at).toLocaleString()}{m.notes ? ` · ${m.notes}` : ""}</div></div>
              <span className={`font-semibold ${m.quantity >= 0 ? "text-[#0B7C5A]" : "text-[#B91C1C]"}`}>{m.quantity >= 0 ? "+" : ""}{m.quantity}</span>
            </div>))}</div>
        </div>)}
    </DialogContent></Dialog>);
}
